import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, of, Subscription } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { GlobalsService } from './globals.service';
import { UserStateService } from './user-state.service';
import { AppStateService } from './app-state.service';
import { ApiService } from './api.service';
import { PathService } from './path.service';
import { PriceService } from './price.service';
import { PermissionsService } from './permissions.service';
import {
  UserInfo,
  UserBalanceInfo,
  LoginCredentials,
  Address,
  ShipmentAddress,
  AppState,
  ImageInfo,
  PartnerInfo
} from '../models';

export interface LoginResponse {
  success: boolean;
  user?: UserInfo;
  error?: string;
  code?: string;
}

/**
 * User Service - converted from UserDelegate.as
 *
 * REST API endpoints (via ApiService):
 * - POST /users/login
 * - POST /users/login-token
 * - POST /users/reset-password
 * - GET  /orders/customer-balance/:id
 * - POST /users/accept-gdpr
 * - POST /users/address
 * - DELETE /users/address/:id
 * - PUT  /users/profile
 * - POST /users/avatar
 */
@Injectable({
  providedIn: 'root'
})
export class UserService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly globals = inject(GlobalsService);
  private readonly userState = inject(UserStateService);
  private readonly appState = inject(AppStateService);
  private readonly api = inject(ApiService);
  private readonly pathService = inject(PathService);
  private readonly priceService = inject(PriceService);
  private readonly permissions = inject(PermissionsService);

  /** Guard against duplicate handleLoginSuccess (checkSession + manual login race) */
  private _loginHandled = false;
  /** Tracks pending checkSession/fallback subscriptions so they can be cancelled */
  private _authSub: Subscription | null = null;

  /**
   * Login with credentials
   * REST: POST /users/login
   */
  login(credentials: LoginCredentials, locale: string): Observable<LoginResponse> {
    // Use HttpClient directly (not ApiService) to get the raw response
    // so we can extract JWT tokens before unwrapResponse strips them
    const url = `${this.globals.apiBaseUrl}/users/login`;
    const requestLocale = locale || this.appState.getLocale();
    return this.http.post<any>(
      url,
      { username: credentials.username, password: credentials.password, locale: requestLocale },
      { headers: { 'X-DBP-LOCALE': requestLocale } }
    ).pipe(
      map(raw => {
        console.log('[UserService] login RAW response:', JSON.stringify(raw));

        // Server returns: { user: { ...fields, auth: { access_token, refresh_token } } }
        const userData = raw?.data?.user || raw?.user || raw?.data;
        const auth = userData?.auth || raw?.auth;
        const token = auth?.access_token || raw?.token || raw?.data?.token;
        const refreshToken = auth?.refresh_token || raw?.refreshToken || raw?.refresh_token
          || raw?.data?.refreshToken || raw?.data?.refresh_token;

        console.log('[UserService] Extracted token:', token ? token.substring(0, 20) + '...' : 'NONE');
        console.log('[UserService] Extracted refreshToken:', refreshToken ? refreshToken.substring(0, 10) + '...' : 'NONE');

        if (token) {
          this.api.setTokens(token, refreshToken || '');
        }

        if (userData) {
          // Sub-user session keys sit BESIDE `user` at the top level
          // (guide §3.1); absent for a plain customer → clears to unrestricted.
          this.permissions.hydrate({
            actingUser: raw?.actingUser ?? raw?.data?.actingUser,
            permissions: raw?.permissions ?? raw?.data?.permissions
          });
          const user = this.mapUserResponse(userData);
          return { success: true, user };
        }

        return { success: false, error: 'No user data in login response' };
      }),
      catchError(error => {
        console.error('[UserService] Login error:', error);
        const msg = error?.error?.message || error?.message || 'Login failed';
        // Stable backend code (INVALID_CREDENTIALS, CUSTOMER_USERS_DISABLED,
        // EXPRESS_V2_DISABLED, …) for i18n mapping in the caller.
        const code = typeof error?.error?.error === 'string' ? error.error.error : undefined;
        return of({ success: false, error: msg, code });
      })
    );
  }

  /**
   * Login with token
   * REST: POST /users/login-token
   */
  loginToken(token: string): Observable<LoginResponse> {
    const formattedToken = token.replace(/[\W_]/g, '').substring(0, 32).toLowerCase();
    const url = `${this.globals.apiBaseUrl}/users/login-token`;
    const requestLocale = this.appState.getLocale();
    return this.http.post<any>(
      url,
      { token: formattedToken, locale: requestLocale },
      { headers: { 'X-DBP-LOCALE': requestLocale } }
    ).pipe(
      map(raw => {
        console.log('[UserService] loginToken RAW response:', JSON.stringify(raw));
        const userData = raw?.data?.user || raw?.user || raw?.data;
        const auth = userData?.auth || raw?.auth;
        const jwt = auth?.access_token || raw?.token || raw?.data?.token;
        const refreshToken = auth?.refresh_token || raw?.refreshToken || raw?.refresh_token
          || raw?.data?.refreshToken || raw?.data?.refresh_token;

        if (jwt) {
          this.api.setTokens(jwt, refreshToken || '');
        }
        if (userData) {
          this.permissions.hydrate({
            actingUser: raw?.actingUser ?? raw?.data?.actingUser,
            permissions: raw?.permissions ?? raw?.data?.permissions
          });
          const user = this.mapUserResponse(userData);
          return { success: true, user };
        }
        return { success: false, error: 'No user data in response' };
      }),
      catchError(error => of({ success: false, error: error?.error?.message || error?.message }))
    );
  }

  /**
   * Request a password-reset email.
   * REST: POST /users/request-password-reset (public)
   * Success: 204 No Content. Errors carry a stable code in body.error
   * (INVALID_LOGIN, AMBIGUOUS_EMAIL, INVALID_USER, NO_RECOVERY_URL,
   * EMAIL_SEND_FAILED, ...) — switch on it in the caller for i18n.
   */
  recover(email: string, locale: string): Observable<{ ok: boolean; error?: string }> {
    return this.api.post('/users/request-password-reset', { email, locale }, { public: true }).pipe(
      map(() => ({ ok: true })),
      catchError(err => of({ ok: false, error: err?.error?.error ?? 'UNKNOWN' }))
    );
  }

  /**
   * Complete a password reset (recovery email or sub-user invite link).
   * REST: POST /users/reset-password (public). Body is { token, newPassword }
   * — the backend routes by token to the customer OR sub-user (guide §8).
   * Success: 204 No Content.
   */
  resetPassword(token: string, newPassword: string): Observable<void> {
    return this.api.post<void>('/users/reset-password', { token, newPassword }, { public: true });
  }

  /**
   * Pre-flight a reset token WITHOUT consuming it, so the reset page can show
   * the dead-link state on load for expired/used links.
   * REST: POST /users/reset-password/validate (public) → 204 when redeemable,
   * 400 { error: INVALID_OR_EXPIRED_TOKEN } when dead. A 5xx is a transient
   * backend fault, NOT a verdict — callers must fail OPEN on it (show the
   * form; submit still validates server-side).
   */
  validateResetToken(token: string): Observable<void> {
    return this.api.post<void>('/users/reset-password/validate', { token }, { public: true });
  }

  /**
   * Plafond balances — the customer's FULL history (spent and expired rows
   * included; filter for display). Offers narrow as plafonds age, so re-read
   * before submitting an extension rather than trusting a cached list.
   * REST: GET /users/balance (the old GET /orders/customer-balance/:id is a
   * compatibility alias of this endpoint).
   */
  getBalance(): Observable<UserBalanceInfo[]> {
    return this.api.get<UserBalanceInfo[]>('/users/balance').pipe(
      map(rows => Array.isArray(rows) ? rows : []),
      catchError(() => of([]))
    );
  }

  /**
   * Accept GDPR
   * REST: POST /users/accept-gdpr
   */
  acceptGDPR(): Observable<string> {
    const user = this.userState.user();
    if (!user) return of('');
    return this.api.post<string>('/users/accept-gdpr');
  }

  /**
   * Save address
   * REST: POST /users/address
   */
  saveAddress(address: ShipmentAddress): Observable<ShipmentAddress | string> {
    const user = this.userState.user();
    if (!user) return of('');
    return this.api.post<ShipmentAddress | string>('/users/address', {
      id: address.id || 0,
      userId: user.id,
      name: address.name || '',
      phone: address.phone || '',
      company: address.company || '',
      address: address.address,
      door: address.door,
      floor: address.floor,
      local: address.local,
      postalCode: address.postalCode,
      cityId: address.city,
      stateId: address.state,
      countryId: address.country,
      mainAddress: address.mainAddress,
      observation: address.observation || ''
    });
  }

  /**
   * Remove address
   * REST: DELETE /users/address/:id
   */
  removeAddress(address: Address): Observable<string> {
    const user = this.userState.user();
    if (!user) return of('');
    return this.api.delete<string>('/users/address/' + (address as any).id);
  }

  /**
   * Edit user profile
   * REST: PUT /users/profile
   */
  editProfile(user: UserInfo, credentials: LoginCredentials): Observable<any> {
    return this.api.put('/users/profile', { user, credentials });
  }

  /**
   * Fresh GET /users/me read for consumers that need up-to-date profile
   * fields outside the boot flow (e.g. commercialName on the quote PDF —
   * legacy/manual login responses may omit presence-gated fields that
   * /users/me serializes). Returns the RAW response; no state mutation.
   */
  getMe(): Observable<any> {
    return this.api.get<any>('/users/me');
  }

  async updateProfile(profileData: Partial<UserInfo>): Promise<void> {
    const user = this.userState.user();
    if (!user) throw new Error('No user logged in');

    const credentials = this.userState.getCredentials();
    if (!credentials) throw new Error('No credentials');

    const updatedUser = { ...user, ...profileData };
    await this.editProfile(updatedUser, credentials).toPromise();
    this.userState.setUser(updatedUser);
  }

  /**
   * Upload avatar
   * REST: POST /users/avatar
   */
  uploadAvatar(avatarBase64: string): Observable<ImageInfo | string> {
    const user = this.userState.user();
    if (!user) return of('');
    return this.api.post<ImageInfo | string>('/users/avatar', { avatar: avatarBase64 });
  }

  logout(): void {
    // Kill the session server-side (one-session-per-user model). The backend
    // resolves SUB-USER sessions from the bearer alone (body ignored), but the
    // customer branch still expects { username, sessionId } (guide §3.5 —
    // verified against UsersController) — send it best-effort. Fire-and-forget
    // BEFORE clearing tokens — after clearTokens the request would 401.
    const user = this.userState.user();
    const body = this.permissions.isSubUser() || !user
      ? {}
      : { username: user.email, sessionId: user.credentials?.sessionId ?? '' };
    this.api.post('/users/logout', body, {
      suppressSessionExpiration: true
    }).pipe(
      catchError(() => of(null))
    ).subscribe();

    this._loginHandled = false;
    this.userState.clear();
    this.userState.setUser(null);
    this.permissions.clear();
    this.globals.setUserType(0);
    this.api.clearTokens();
    this.clearLocalStorage();
    sessionStorage.removeItem('postLoginRedirect');
    // Manual hide-prices toggle must not leak into the next session.
    this.appState.resetHidePrices();
    this.appState.setState(AppState.LOGIN);
  }

  checkSession(): void {
    // Try JWT token restore first
    if (this.api.restoreTokens()) {
      this.fetchBootUser();
      return;
    }
    this.fallbackToLegacySession();
  }

  /**
   * Boot-time GET /users/me with the active tokens. `adminRetry` marks the
   * second attempt after a dead impersonation session was swapped back to
   * the parked admin — that attempt is final (no further promotion).
   */
  private fetchBootUser(adminRetry = false): void {
    // While impersonating, the active slot is access-token-only (the backend
    // mints no refresh token for /support/login), so the refresh-and-notify
    // dance in ApiService.handleError can only fail and pop a spurious
    // "session expired" dialog — suppress it and let the 401 fall through
    // to the promote-admin path below instead.
    const options = !adminRetry && this.userState.isSupportSession()
      ? { suppressSessionExpiration: true }
      : undefined;
    // GET /users/me returns the authenticated user (validates the JWT via requireAuth
    // server-side). NOTE: /users/profile only accepts PUT/PATCH — it's the update-profile
    // endpoint, not the read endpoint. Using it returns 405 Method Not Allowed.
    this._authSub = this.api.get<any>('/users/me', options).subscribe({
      next: (userData) => {
        if (userData) {
          // /me is the boot source of truth for the sub-user session keys
          // (guide §3.4) — they ride on the user object itself.
          this.permissions.hydrate(userData);
          const user = this.mapUserResponse(userData);
          this.handleLoginSuccess(user);
          // sessionReady is set inside handleLoginSuccess
        } else {
          this.api.clearTokens();
          this.fallbackToLegacySession();
        }
      },
      error: (err) => {
        const status = Number(err?.status);
        if (status === 401 || status === 403) {
          // Mid-impersonation reload with a dead customer token (1h TTL, no
          // refresh by design): promote the parked admin session and retry
          // /users/me as the admin instead of clearing both sessions — the
          // admin slot has a real refresh token, so it recovers even when
          // the admin JWT is stale too.
          if (!adminRetry && this.userState.isSupportSession() && this.api.endSupportSession()) {
            console.warn('[UserService] Impersonation token rejected on boot — restoring the parked admin session');
            this.fetchBootUser(true);
            return;
          }
          this.api.clearTokens();
        } else {
          // Only drop the tokens on a genuine auth rejection (401/403).
          // A transient failure — network blip, 5xx, or an ABORTED request
          // (e.g. a popup that called /users/me then closed) reports status
          // 0, and must NOT clear the shared-localStorage tokens, or it logs
          // every window out. Keep them so the next load can retry.
          console.warn('[UserService] checkSession /users/me transient failure (status', status, ') — keeping tokens');
        }
        this.fallbackToLegacySession();
      }
    });
  }

  private fallbackToLegacySession(): void {
    const savedCredentials = this.getLocalCredentials();
    const keepSession = localStorage.getItem('keepSession') === 'true';

    if (keepSession && savedCredentials) {
      this.userState.setLocalCredentials(savedCredentials);
      this.userState.setRememberSelected(true);
      this.userState.setKeepSession(true);

      // Auto login
      this._authSub = this.login(savedCredentials, this.appState.locale()).subscribe(response => {
        if (response.success && response.user) {
          this.handleLoginSuccess(response.user);
          // sessionReady is set inside handleLoginSuccess
        } else {
          this.appState.setState(AppState.LOGIN);
          this.userState.setSessionReady();
        }
      });
    } else {
      const savedUser = this.getLocalUser();
      if (savedUser) {
        this.userState.setLocalUser(savedUser);
        this.userState.setRememberSelected(true);
        this.userState.setKeepSession(false);
      }
      this.appState.setState(AppState.LOGIN);
      this.userState.setSessionReady();
    }
  }

  async handleLoginSuccess(user: UserInfo): Promise<void> {
    // Cancel any pending checkSession/fallback auth API calls to prevent
    // their callbacks from clearing tokens or triggering duplicate processing
    this._authSub?.unsubscribe();
    this._authSub = null;

    // Prevent duplicate processing when checkSession auto-login and manual login race
    if (this._loginHandled && this.userState.isLoggedIn()) {
      console.log('[UserService] handleLoginSuccess already processed, skipping duplicate');
      return;
    }
    this._loginHandled = true;

    console.log('[UserService] handleLoginSuccess:', user);
    console.log('[UserService] User brands:', user.brands);
    console.log('[UserService] User partnerId:', user.partnerId);

    // Set user in state
    this.userState.setUser(user);

    // Update globals. During a rehydrated support session (reload mid-
    // impersonation) the human at the keyboard is still the parked admin —
    // mirror handleSupportLogin's deliberate setUserType skip and keep the
    // role observables on the ADMIN's typeId, or the topbar support icon and
    // order-item repair toolbar vanish on refresh. Brands still follow the
    // active user (visual only, not permission-bearing).
    const parkedAdmin = this.userState.originalUser();
    this.globals.setUserType(
      this.userState.isSupportSession() && parkedAdmin ? parkedAdmin.typeId : user.typeId
    );
    this.globals.setBrands(user.brands || []); // Always call with at least empty array

    // Setup addresses
    if (user.addresses && user.addresses.length > 0) {
      const addresses = user.addresses.map(addr => ({
        ...addr,
        saved: true,
        allowSelect: true,
        allowEdit: user.addressesPermissionType === 0
      })) as ShipmentAddress[];
      addresses[0].selected = true;
      this.userState.setAddresses(addresses);
    } else {
      const newAddress: ShipmentAddress = {
        id: 0,
        address: '',
        door: '',
        floor: '',
        local: '',
        postalCode: '',
        city: -1,
        state: -1,
        country: -1,
        mainAddress: 0,
        saved: false,
        allowSelect: true,
        selected: true,
        allowEdit: true
      };
      this.userState.setAddresses([newAddress]);
    }

    // Set balance
    if (user.balance) {
      this.userState.setBalance(user.balance);
    }

    // Initialize PathService before marking session ready — critical for photo URLs
    await this.pathService.init().catch(err => {
      console.error('[UserService] PathService init failed:', err);
    });

    // Load additional data after login (like legacy app does)
    this.loadPostLoginData(user);

    // Signal that session check is complete (auth guard can proceed)
    this.userState.setSessionReady();

    // Navigate to home based on brands
    if (this.globals.hasDreambooksPro) {
      this.appState.setState(AppState.HOME_DREAMBOOKSPRO);
    } else if (this.globals.hasOneLabPro) {
      this.appState.setState(AppState.HOME_ONELABPRO);
    } else if (this.globals.hasLfmPro) {
      this.appState.setState(AppState.HOME_LFMPRO);
    }

    let redirectUrl = sessionStorage.getItem('postLoginRedirect');
    if (redirectUrl) {
      sessionStorage.removeItem('postLoginRedirect');
      redirectUrl = this.appState.buildLocaleUrl(redirectUrl);
      this.router.navigateByUrl(redirectUrl);
      return;
    }

    // Navigate to home page
    this.router.navigate(this.appState.buildLocaleCommands(['home']));
  }

  /**
   * Handle support/impersonation login - mirrors handleLoginSuccess() but preserves original user.
   * Called from SupportDialogComponent when a support user logs in as a customer.
   */
  async handleSupportLogin(rawUserData: any): Promise<UserInfo> {
    // /support/login returns { user, auth, impersonatedBy, _token } in the
    // current API. Older deployments returned the customer fields flat at
    // the top level (no `.user` wrapper). Accept both so a backend rollback
    // doesn't break impersonation.
    const userPayload = rawUserData?.user ?? rawUserData;
    // Impersonation targets a customer — no actingUser → clears to unrestricted.
    this.permissions.hydrate(rawUserData);
    const user = this.mapUserResponse(userPayload);

    // Park admin tokens + swap to customer's atomically. The previous flow
    // (setTokens then startSupportSession) lost the admin tokens because
    // startSupportSession used to call clear(), wiping them seconds after
    // they were set. api.startSupportSession owns both the in-memory swap
    // and the localStorage write so the two can never desync.
    const auth = rawUserData?.auth;
    this.api.startSupportSession(user, {
      access: auth?.access_token || '',
      refresh: auth?.refresh_token || ''
    });

    // Initialize globals like normal login — but DELIBERATELY skip
    // setUserType. The active user is the customer (typeId NORMAL), but
    // the human at the keyboard is still an admin/support agent. Calling
    // setUserType(customer.typeId) would clobber globals.isSupport$ to
    // false, hiding the topbar support icon and the order-item repair
    // toolbar precisely when the agent needs them most. Leaving the
    // observables untouched keeps them reflecting the admin's role for
    // the duration of the impersonation. On exit, app.ts.onExitSupportSession
    // calls setUserType(originalUser.typeId) to put things back. Brands
    // continue to follow the customer (visual only, not permission-bearing).
    this.globals.setBrands(user.brands || []);

    // Setup addresses
    if (user.addresses && user.addresses.length > 0) {
      const addresses = user.addresses.map(addr => ({
        ...addr,
        saved: true,
        allowSelect: true,
        allowEdit: user.addressesPermissionType === 0
      })) as ShipmentAddress[];
      addresses[0].selected = true;
      this.userState.setAddresses(addresses);
    }

    // Set balance
    if (user.balance) {
      this.userState.setBalance(user.balance);
    }

    // Initialize PathService for photo URLs
    await this.pathService.init().catch(err => {
      console.error('[UserService] PathService init failed during support login:', err);
    });

    // Load post-login data (currencies, drafts, notifications, projects)
    this.loadPostLoginData(user);

    // Set correct app state based on brands
    if (this.globals.hasDreambooksPro) {
      this.appState.setState(AppState.HOME_DREAMBOOKSPRO);
    } else if (this.globals.hasOneLabPro) {
      this.appState.setState(AppState.HOME_ONELABPRO);
    } else if (this.globals.hasLfmPro) {
      this.appState.setState(AppState.HOME_LFMPRO);
    }

    console.log('[UserService] Support login complete as:', user.email, 'brands:', user.brands);
    return user;
  }

  /**
   * Exit the support impersonation session and restore the admin's data.
   *
   * Entering impersonation wiped the customer-scoped caches (addresses,
   * projects, drafts…) so the customer's data could be loaded fresh.
   * Exiting also wipes them — without re-fetching for the restored admin,
   * downstream views (cart's address list, projects list, drafts list)
   * render empty until the next full login. We had a real user report of
   * the cart losing its addresses post-exit; this is the fix.
   */
  exitSupportSession(): UserInfo | null {
    const originalUser = this.api.endSupportSession();
    if (!originalUser) return null;
    this.globals.setUserType(originalUser.typeId);
    this.loadPostLoginData(originalUser);
    return originalUser;
  }

  /**
   * Load additional data after login - mimics legacy app behavior
   * REST calls: drafts, notifications, partner, projects
   */
  private loadPostLoginData(user: UserInfo): void {
    console.log('[UserService] loadPostLoginData - starting... user currencyId:', user.currencyId);

    // Load currencies and initialize for user
    this.priceService.loadCurrencies().then(() => {
      console.log('[UserService] Currencies loaded, initializing for user with currencyId:', user.currencyId);
      // Prefer the user's authoritative currency object — the global
      // /common/currencies list may not include it (e.g. USD), in which case
      // setCurrency(id) would fall back to EUR. Fall back to id-lookup only
      // when the object isn't present (legacy payloads).
      if (user.currency) {
        this.priceService.useCurrency(user.currency);
      } else {
        this.priceService.setCurrency(user.currencyId);
      }
      this.priceService.setDisplayCurrency(user.displayCurrencyId || user.currencyId);
    }).catch(err => {
      console.error('[UserService] Error loading currencies:', err);
    });

    // Drafts + projects are PROJECTS-gated server-side — skip both for a
    // sub-user without the module, or every login fires guaranteed 403s that
    // the permission interceptor surfaces as a spurious "no access" toast.
    const canLoadProjects = this.permissions.canProjects();
    if (!canLoadProjects) {
      this.userState.setOrdersDrafts([]);
      this.userState.setProjects([]);
    }

    // Load order drafts
    if (canLoadProjects) this.api.get<any>('/drafts').pipe(
      map(response => {
        const items = response?.items || (Array.isArray(response) ? response : []);
        return items.map((item: any) => ({
          ...item,
          creationDateString: item.creationDateString || this.formatApiDate(item.creationDate),
          lastModifiedDateString: item.lastModifiedDateString || this.formatApiDate(item.lastModificationDate),
          thumbnail: item.thumbnail || item.thumbnailUrl || null,
        }));
      }),
      catchError(err => {
        console.error('[UserService] Error loading drafts:', err);
        return of([]);
      })
    ).subscribe(drafts => {
      console.log('[UserService] Order drafts loaded:', drafts?.length || 0);
      this.userState.setOrdersDrafts(drafts || []);
    });

    // Load partner if user has partnerId
    if (user.partnerId && user.partnerId > 0) {
      this.api.get<PartnerInfo>('/partners/' + user.partnerId).pipe(
        catchError(err => {
          console.error('[UserService] Error loading partner:', err);
          return of(null);
        })
      ).subscribe(partner => {
        console.log('[UserService] Partner loaded:', partner);
        if (partner) {
          this.appState.setPartner(partner);
        }
      });
    }

    // Load projects
    if (canLoadProjects) this.api.get<any[]>('/projects').pipe(
      catchError(err => {
        console.error('[UserService] Error loading projects:', err);
        return of([]);
      })
    ).subscribe(projects => {
      console.log('[UserService] Projects loaded:', projects?.length || 0);
      this.userState.setProjects(projects || []);
    });

    // Load addresses from dedicated endpoint
    this.getAddresses().subscribe(addresses => {
      if (addresses.length > 0) {
        addresses[0].selected = true;
        const canEdit = (user?.addressesPermissionType ?? 0) === 0;
        addresses.forEach(a => a.allowEdit = canEdit);
        this.userState.setAddresses(addresses);
        console.log('[UserService] Addresses loaded:', addresses.length);
      }
    });
  }

  /**
   * Fetch addresses from dedicated endpoint
   * REST: GET /users/addresses
   */
  getAddresses(): Observable<ShipmentAddress[]> {
    return this.api.get<any>('/users/addresses').pipe(
      map(res => {
        const items = res?.items || [];
        return items.map((addr: any) => ({
          id: addr.id,
          name: addr.name || '',
          phone: addr.phone || '',
          company: addr.company || '',
          address: addr.address || '',
          door: addr.door || '',
          floor: addr.floor || '',
          local: addr.local || '',
          postalCode: addr.postalCode || '',
          country: addr.countryId ?? -1,
          state: addr.stateId ?? -1,
          city: addr.cityId ?? -1,
          mainAddress: addr.isDefault ? 1 : 0,
          saved: true,
          allowSelect: true,
          allowEdit: true
        })) as ShipmentAddress[];
      }),
      catchError(() => of([]))
    );
  }

  saveLocalCredentials(credentials: LoginCredentials, keepSession: boolean): void {
    if (keepSession) {
      localStorage.setItem('credentials', JSON.stringify(credentials));
      localStorage.setItem('keepSession', 'true');
      const jwt = this.userState.jwtToken();
      const refresh = this.userState.refreshToken();
      if (jwt && refresh) {
        localStorage.setItem('jwt', jwt);
        localStorage.setItem('refreshToken', refresh);
      }
    }
  }

  saveLocalUser(user: UserInfo): void {
    // In a sub-user session `user` is the PARENT customer — remember the
    // sub-user's own identity instead, or the next "remembered" login would
    // submit the parent's email with the sub-user's password.
    const acting = this.permissions.actingUser();
    const shareUser = acting ? {
      email: acting.email,
      // filter(Boolean) — firstName/lastName may be null for email-only
      // members; template-literal interpolation would render "null null".
      name: [acting.firstName, acting.lastName].filter(Boolean).join(' ') || acting.email,
      avatar: undefined
    } : {
      email: user.email,
      name: `${user.firstName} ${user.lastName}`,
      avatar: user.avatar
    };
    localStorage.setItem('localUser', JSON.stringify(shareUser));
  }

  private getLocalCredentials(): LoginCredentials | null {
    const saved = localStorage.getItem('credentials');
    return saved ? JSON.parse(saved) : null;
  }

  private getLocalUser(): any {
    const saved = localStorage.getItem('localUser');
    return saved ? JSON.parse(saved) : null;
  }

  private clearLocalStorage(): void {
    localStorage.removeItem('credentials');
    localStorage.removeItem('localUser');
    localStorage.removeItem('keepSession');
    localStorage.removeItem('jwt');
    localStorage.removeItem('refreshToken');
  }

  private mapUserResponse(response: any): UserInfo {
    // The REST login response carries the currency as a nested object
    // (currency: { id, code, symbol... }), not a flat currencyId. Accept
    // both shapes — reading only the flat field silently fell back to 1
    // (EUR) and BRL users saw € formatting on BRL amounts.
    const currencyId = parseInt(String(response.currencyId ?? response.currency?.id), 10) || 1;
    const displayCurrencyId = parseInt(String(response.displayCurrencyId), 10) || currencyId;
    // Keep the full currency object — it's authoritative (carries code/symbol/
    // separators) and may describe a currency absent from the global
    // /common/currencies list (e.g. USD), which is exactly when re-deriving
    // by id falls back to EUR.
    const currency = response.currency ? {
      id: Number(response.currency.id),
      code: response.currency.code || '',
      symbol: response.currency.symbol || '',
      fractionalDigits: Number(response.currency.fractionalDigits ?? 2),
      decimalSeparator: response.currency.decimalSeparator || ',',
      groupingSeparator: response.currency.groupingSeparator || '.',
      useGrouping: Number(response.currency.useGrouping ?? 1)
    } : undefined;
    console.log('[UserService] mapUserResponse - raw currencyId:', response.currencyId, '-> parsed:', currencyId, ', displayCurrencyId:', response.displayCurrencyId, '-> parsed:', displayCurrencyId, ', currency:', currency?.code);
    return {
      id: response.id,
      typeId: response.typeId || 0,
      company: response.company,
      // Conditional spread keeps the key ABSENT when the backend doesn't
      // serialize it — the commercial-name surfaces presence-gate on the key.
      ...('commercialName' in (response || {}) ? { commercialName: response.commercialName ?? '' } : {}),
      firstName: response.firstName || '',
      lastName: response.lastName || '',
      email: response.email || '',
      phone: response.phone || '',
      avatar: response.avatar,
      currencyId: currencyId,
      displayCurrencyId: displayCurrencyId,
      currency: currency,
      discountType: response.discountType,
      vatTab: response.vatTab,
      vatRegime: response.vatRegime,
      recargoTax: response.recargoTax,
      priceTab: response.priceTab,
      credentials: response.credentials || { username: '', password: 'encrypted', trial: 0, update: 0 },
      token: response.token,
      showPrice: response.showPrice,
      paymentPermissions: response.paymentPermissions,
      allowOrderWithoutPrice: response.allowOrderWithoutPrice,
      allowOrdersGrouping: !!response.allowOrdersGrouping,
      allowOrderIssues: response.allowOrderIssues || false,
      allowGalleries: response.allowGalleries || false,
      billingAddress: response.billingAddress,
      countryId: response.countryId != null ? Number(response.countryId) : undefined,
      billingCountryId: response.billingCountryId != null ? Number(response.billingCountryId) : undefined,
      addresses: response.addresses,
      addressesPermissionType: response.addressesPermissionType || 0,
      shipmentTypeRef: response.shipmentTypeRef,
      brands: response.brands || [],
      partnerId: response.partnerId || 0,
      origin: response.origin || 0,
      location: response.location || 0,
      balance: response.balance,
      points: response.points ?? -1,
      hasCollectionRequest: response.hasCollectionRequest || false,
      hasAlbumDesign: response.hasAlbumDesign || false,
      allowCustomerUsers: response.allowCustomerUsers === true,
      gdprEligible: response.gdprEligible ?? true,
      acceptedGDPR: response.acceptedGDPR ?? response.acceptedGdpr ?? false,
      vatAlert: response.vatAlert || false
    };
  }

  private formatApiDate(dateStr: string | null | undefined): string {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      const pad = (n: number) => n.toString().padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    } catch {
      return dateStr || '';
    }
  }
}
